import type { BmiCalculatorState, BmiImperialHeightMode, BmiImperialWeightMode, BmiResult, BmiUnitSystem } from "../types";

const centimetresPerInch = 2.54;
const poundsPerKilogram = 2.2046226218;
const poundsPerStone = 14;

function parsePositiveNumber(value: string) {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
}

function formatWholeNumber(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) {
    return "";
  }

  return String(Math.round(value));
}

function roundBmi(value: number) {
  return Math.round(value * 10) / 10;
}

function formatAdviceAmount(value: number) {
  const roundedValue = Math.ceil(value);
  return roundedValue < 1 ? 1 : roundedValue;
}

export function normalizeMeasurementInput(value: string) {
  const cleanedValue = value.replace(/[^\d]/g, "");

  if (!cleanedValue) {
    return "";
  }

  return String(Number(cleanedValue));
}

export function calculateBmi(state: BmiCalculatorState): BmiResult | null {
  const measurements = getActiveMeasurements(state);

  if (!measurements) {
    return null;
  }

  const heightMetres = measurements.heightCentimetres / 100;
  const bmi = measurements.weightKilograms / (heightMetres * heightMetres);

  return classifyBmi(roundBmi(bmi), measurements, state.unitSystem);
}

export function convertUnitSystem(state: BmiCalculatorState, unitSystem: BmiUnitSystem): BmiCalculatorState {
  const measurements = getActiveMeasurements(state);

  if (!measurements) {
    return { ...state, unitSystem };
  }

  return {
    ...state,
    ...fieldsFromMeasurements(measurements.heightCentimetres, measurements.weightKilograms),
    unitSystem
  };
}

export function convertImperialHeightMode(state: BmiCalculatorState, imperialHeightMode: BmiImperialHeightMode): BmiCalculatorState {
  const totalInches = getImperialHeightInches(state);

  if (totalInches === null) {
    return { ...state, imperialHeightMode };
  }

  return {
    ...state,
    ...imperialHeightFieldsFromInches(totalInches),
    imperialHeightMode
  };
}

export function convertImperialWeightMode(state: BmiCalculatorState, imperialWeightMode: BmiImperialWeightMode): BmiCalculatorState {
  const totalPounds = getImperialWeightPounds(state);

  if (totalPounds === null) {
    return { ...state, imperialWeightMode };
  }

  return {
    ...state,
    ...imperialWeightFieldsFromPounds(totalPounds),
    imperialWeightMode
  };
}

export function syncMeasurementFields(state: BmiCalculatorState, changedField: keyof BmiCalculatorState): BmiCalculatorState {
  const normalizedState = {
    ...state,
    [changedField]: normalizeMeasurementInput(String(state[changedField]))
  };

  if (changedField === "heightCentimetres") {
    const heightCentimetres = parsePositiveNumber(normalizedState.heightCentimetres);
    return { ...normalizedState, ...imperialHeightFieldsFromCentimetres(heightCentimetres) };
  }

  if (changedField === "weightKilograms") {
    const weightKilograms = parsePositiveNumber(normalizedState.weightKilograms);
    return { ...normalizedState, ...imperialWeightFieldsFromKilograms(weightKilograms) };
  }

  if (changedField === "heightFeet" || changedField === "heightInches" || changedField === "heightTotalInches") {
    const totalInches = getImperialHeightInches(normalizedState);
    return { ...normalizedState, ...metricHeightFieldsFromInches(totalInches), ...imperialHeightFieldsFromInches(totalInches) };
  }

  if (changedField === "weightPounds" || changedField === "weightStones" || changedField === "weightStonePounds") {
    const totalPounds = getImperialWeightPounds(normalizedState);
    return { ...normalizedState, ...metricWeightFieldsFromPounds(totalPounds), ...imperialWeightFieldsFromPounds(totalPounds) };
  }

  return normalizedState;
}

function getActiveMeasurements(state: BmiCalculatorState) {
  if (state.unitSystem === "metric") {
    const heightCentimetres = parsePositiveNumber(state.heightCentimetres);
    const weightKilograms = parsePositiveNumber(state.weightKilograms);

    if (heightCentimetres === null || weightKilograms === null) {
      return null;
    }

    return { heightCentimetres, weightKilograms };
  }

  const heightInches = getImperialHeightInches(state);
  const weightPounds = getImperialWeightPounds(state);

  if (heightInches === null || weightPounds === null) {
    return null;
  }

  return {
    heightCentimetres: heightInches * centimetresPerInch,
    weightKilograms: weightPounds / poundsPerKilogram
  };
}

function getImperialHeightInches(state: BmiCalculatorState) {
  if (state.imperialHeightMode === "inches") {
    return parsePositiveNumber(state.heightTotalInches);
  }

  const heightFeet = parsePositiveNumber(state.heightFeet) ?? 0;
  const heightInches = parsePositiveNumber(state.heightInches) ?? 0;
  const totalHeightInches = heightFeet * 12 + heightInches;

  return totalHeightInches > 0 ? totalHeightInches : null;
}

function getImperialWeightPounds(state: BmiCalculatorState) {
  if (state.imperialWeightMode === "stones-pounds") {
    const weightStones = parsePositiveNumber(state.weightStones) ?? 0;
    const weightStonePounds = parsePositiveNumber(state.weightStonePounds) ?? 0;
    const totalWeightPounds = weightStones * poundsPerStone + weightStonePounds;

    return totalWeightPounds > 0 ? totalWeightPounds : null;
  }

  return parsePositiveNumber(state.weightPounds);
}

function fieldsFromMeasurements(heightCentimetres: number, weightKilograms: number) {
  return {
    heightCentimetres: formatWholeNumber(heightCentimetres),
    weightKilograms: formatWholeNumber(weightKilograms),
    ...imperialHeightFieldsFromCentimetres(heightCentimetres),
    ...imperialWeightFieldsFromKilograms(weightKilograms)
  };
}

function imperialHeightFieldsFromCentimetres(heightCentimetres: number | null) {
  return imperialHeightFieldsFromInches(heightCentimetres === null ? null : heightCentimetres / centimetresPerInch);
}

function imperialHeightFieldsFromInches(heightInches: number | null) {
  if (heightInches === null) {
    return {
      heightFeet: "",
      heightInches: "",
      heightTotalInches: ""
    };
  }

  const totalInches = Math.round(heightInches);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;

  return {
    heightFeet: formatWholeNumber(feet),
    heightInches: String(inches),
    heightTotalInches: formatWholeNumber(totalInches)
  };
}

function metricHeightFieldsFromInches(heightInches: number | null) {
  return {
    heightCentimetres: formatWholeNumber(heightInches === null ? null : heightInches * centimetresPerInch)
  };
}

function imperialWeightFieldsFromKilograms(weightKilograms: number | null) {
  return imperialWeightFieldsFromPounds(weightKilograms === null ? null : weightKilograms * poundsPerKilogram);
}

function imperialWeightFieldsFromPounds(weightPounds: number | null) {
  if (weightPounds === null) {
    return {
      weightPounds: "",
      weightStones: "",
      weightStonePounds: ""
    };
  }

  const totalPounds = Math.round(weightPounds);
  const stones = Math.floor(totalPounds / poundsPerStone);
  const remainingPounds = totalPounds % poundsPerStone;

  return {
    weightPounds: formatWholeNumber(totalPounds),
    weightStones: formatWholeNumber(stones),
    weightStonePounds: String(remainingPounds)
  };
}

function metricWeightFieldsFromPounds(weightPounds: number | null) {
  return {
    weightKilograms: formatWholeNumber(weightPounds === null ? null : weightPounds / poundsPerKilogram)
  };
}

function classifyBmi(value: number, measurements: { heightCentimetres: number; weightKilograms: number }, unitSystem: BmiUnitSystem): BmiResult {
  if (value < 18.5) {
    return {
      value,
      category: "Underweight",
      tone: "low",
      message: "This is below the healthy adult BMI range.",
      advice: buildAdvice(measurements, unitSystem, 18.5, "gain")
    };
  }

  if (value < 25) {
    return {
      value,
      category: "Healthy weight",
      tone: "healthy",
      message: "This sits inside the healthy adult BMI range.",
      advice: null
    };
  }

  if (value < 30) {
    return {
      value,
      category: "Overweight",
      tone: "high",
      message: "This is above the healthy adult BMI range.",
      advice: buildAdvice(measurements, unitSystem, 24.9, "lose")
    };
  }

  return {
    value,
    category: "Obesity range",
    tone: "very-high",
    message: "This is in the highest adult BMI range.",
    advice: buildAdvice(measurements, unitSystem, 29.9, "lose")
  };
}

function buildAdvice(
  measurements: { heightCentimetres: number; weightKilograms: number },
  unitSystem: BmiUnitSystem,
  targetBmi: number,
  direction: "gain" | "lose"
) {
  const heightMetres = measurements.heightCentimetres / 100;
  const targetWeightKilograms = targetBmi * heightMetres * heightMetres;
  const changeKilograms =
    direction === "gain" ? targetWeightKilograms - measurements.weightKilograms : measurements.weightKilograms - targetWeightKilograms;

  if (changeKilograms <= 0) {
    return null;
  }

  if (unitSystem === "imperial") {
    const pounds = formatAdviceAmount(changeKilograms * poundsPerKilogram);
    return direction === "gain"
      ? `Advice: gain about ${pounds} lb to reach the healthy BMI range.`
      : `Advice: lose about ${pounds} lb to reach the next BMI category.`;
  }

  const kilograms = formatAdviceAmount(changeKilograms);
  return direction === "gain"
    ? `Advice: gain about ${kilograms} kg to reach the healthy BMI range.`
    : `Advice: lose about ${kilograms} kg to reach the next BMI category.`;
}
